/**
 * Viewer seek-reversion guard (WS-8).
 *
 * Viewers are not allowed to scrub. If a viewer (or a stray player-internal
 * seek) moves `currentTime` away from the authoritative position by more than
 * {@link SYNC.SEEK_GUARD_TOLERANCE_S}, snap it back. This is a *UX* guard only
 * — the server remains the real authority on playback state; nothing here is
 * security-relevant (see the WHY comment on `applyGuard`).
 *
 * Two detection paths, because video.js does not reliably fire `seeking`:
 *   1. The `seeking` event — primary.
 *   2. A `timeupdate` poll — backstop for video.js #3936, where no seek events
 *      fire while paused at exactly `currentTime === 0`. We also seed a
 *      non-zero baseline on mount so the player is never stuck at 0.
 *
 * The programmatic-flag ref is shared with `useRoomSync` so that
 * server-driven (programmatic) seeks are not re-snapped — that would fight
 * the sync engine and create a feedback loop.
 *
 * @see video.js #3936 — seek events missing while paused at currentTime 0.
 */
import { useEffect, type MutableRefObject } from 'react';
import { SYNC } from '@syncreel/shared';
import type { Player } from '../player/useVideoJs';
import type { Role } from '@syncreel/shared';

/** Inputs to {@link useSeekGuard}. */
export interface UseSeekGuardArgs {
  /** The video.js player (null until ready). */
  player: Player | null;
  /** Current room role; the guard is active only for viewers. */
  role: Role;
  /** Shared programmatic-flag ref (true while a seek is sync-driven). */
  programmaticRef: MutableRefObject<boolean>;
  /** Live authoritative position in seconds (projected to server-now). */
  getAuthoritativePosition: () => number;
}

// WHY: video.js #3936 — a player paused at exactly currentTime === 0 never
// fires `seeking`, so the guard could not detect a scrub from 0 to t. We nudge
// the element off zero by a sub-millisecond epsilon so seeks register. The
// epsilon is far inside SYNC.IGNORE_BAND_S and invisible to the user.
const SEEKBOT_SEED_S = 0.001;

/**
 * Install the viewer seek guard on `player`. No-op for hosts and until the
 * player is ready. Re-installs when the player or role changes.
 */
export function useSeekGuard(args: UseSeekGuardArgs): void {
  const { player, role, programmaticRef, getAuthoritativePosition } = args;

  useEffect(() => {
    if (!player || role !== 'viewer') return;

    let raf: ReturnType<typeof setInterval> | null = null;

    const applyGuard = (fromPoll: boolean): void => {
      // WHY: never rely on the client guard for security — the server is the
      // real gate. This only prevents visible UI drift for honest viewers.
      if (programmaticRef.current) return;

      const authoritative = getAuthoritativePosition();
      const current = player.currentTime() ?? 0;
      const delta = Math.abs(current - authoritative);

      if (delta > SYNC.SEEK_GUARD_TOLERANCE_S) {
        // Mark programmatic so our own snap-back does not echo as a host action
        // (hosts are not here, but the flag is shared and keeps semantics sane)
        // and so the `seeking`/`timeupdate` it triggers is ignored once.
        programmaticRef.current = true;
        // WHY: if the authority is exactly 0, re-seed to the non-zero baseline
        // so the player is not stuck at 0 again (#3936).
        const target = authoritative === 0 ? SEEKBOT_SEED_S : authoritative;
        player.currentTime(target);
        // 1s failsafe: clear the flag even if no `seeked` event arrives.
        window.setTimeout(() => {
          programmaticRef.current = false;
        }, 1000);
      } else if (fromPoll && current === 0 && authoritative === 0) {
        // WHY: #3936 — seed a non-zero baseline when parked at exactly 0.
        programmaticRef.current = true;
        player.currentTime(SEEKBOT_SEED_S);
        window.setTimeout(() => {
          programmaticRef.current = false;
        }, 1000);
      }
    };

    const onSeeking = (): void => applyGuard(false);

    // WHY: timeupdate is the backstop for #3936 (no seeking event while paused
    // at 0). It fires ~4×/s during playback and occasionally while paused, so
    // it catches drift that `seeking` misses without being expensive.
    const onTimeUpdate = (): void => applyGuard(true);

    player.on('seeking', onSeeking);
    player.on('timeupdate', onTimeUpdate);

    // Seed the non-zero baseline immediately if the player starts at 0.
    if (player.currentTime() === 0) {
      programmaticRef.current = true;
      player.currentTime(SEEKBOT_SEED_S);
      window.setTimeout(() => {
        programmaticRef.current = false;
      }, 1000);
    }

    // Independent poll backstop in case timeupdate is also quiet (paused at 0
    // on some browsers fires neither seeking nor timeupdate reliably).
    raf = setInterval(() => applyGuard(true), SYNC.HEARTBEAT_MS);

    return () => {
      player.off('seeking', onSeeking);
      player.off('timeupdate', onTimeUpdate);
      if (raf !== null) clearInterval(raf);
    };
  }, [player, role, programmaticRef, getAuthoritativePosition]);
}
